import { CLARE_HOME_HAM_METHODS, ClareHomeRPCServiceNames, HAM2_METHODS } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { Observable, combineLatest, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
    ApnsCommandHelper,
    CommandType,
    DcoOperationCommandHelper,
    DcoPropertyCommandHelper,
    EmailCommandHelper,
    ServiceOperationCommandHelper,
    ServicePropertyCommandHelper,
} from "../model/commands";
import { CommandHelper, CommandProviders } from "../model/commands/command";
import { SceneCommandHelper, isSceneCommand } from "../model/commands/scene-command";
import { ConditionType, MultiConditionHelper, PropertyChangeConditionHelper, UIConditionHelper } from "../model/conditions";
import { ConditionHelper, ConditionProviders } from "../model/conditions/condition";
import { AbsoluteTimerConditionHelper, TimerConditionHelper } from "../model/conditions/timer-condition";
import { CHRule, CHRuleInt } from "../model/rule";

import { Injectable } from "@angular/core";
import { ClassImporterExporter } from "@app/shared/resources/class-importer-exporter.service";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { EquatorProxyService } from "@app/shared/services/equator-api/equator-proxy";
import { CHRulesService } from "./ch-rules.service";

export const conditionHelperMap = {
    [ConditionProviders.MULTI]: MultiConditionHelper,
    [ConditionProviders.UI_CONDITION]: UIConditionHelper,
    [ConditionProviders.PROPERTY_CHANGE]: PropertyChangeConditionHelper,
    [ConditionProviders.CUSTOM_BUTTON]: ConditionHelper,
    [ConditionProviders.ASTRO_TIMER]: TimerConditionHelper,
    [ConditionProviders.ABSOLUTE_TIMER]: AbsoluteTimerConditionHelper,
    [ConditionProviders.DAILY_TIMER]: TimerConditionHelper,
    [ConditionProviders.DAILY_PERIOD]: ConditionHelper,
    [ConditionProviders.EVENT]: ConditionHelper,
    [ConditionProviders.LOGIC]: ConditionHelper,
    [ConditionProviders.STATUS]: ConditionHelper,
    [ConditionProviders.RC_CONDITION]: ConditionHelper,
};

export const commandHelperMap = {
    [CommandProviders.SCENE]: SceneCommandHelper,
    [CommandProviders.SERVICE_PROPERTY]: ServicePropertyCommandHelper,
    [CommandProviders.SERVICE_OPERATION]: ServiceOperationCommandHelper,
    [CommandProviders.PUSH]: ApnsCommandHelper,
    [CommandProviders.EMAIL]: EmailCommandHelper,
    [CommandProviders.DCO_OPERATION]: DcoOperationCommandHelper,
    [CommandProviders.DCO_PROPERTY]: DcoPropertyCommandHelper,
    [CommandProviders.GROUP]: CommandHelper,
    [CommandProviders.FILTER_RULE_STATE]: CommandHelper,
    [CommandProviders.RULE_STATE]: CommandHelper,
    [CommandProviders.LOG]: CommandHelper,
    [CommandProviders.EVENT]: CommandHelper,
};

@Injectable({
    providedIn: "root",
})
export class CHRulesDbService {
    constructor(private equatorProxyService: EquatorProxyService, private readonly ruleService: CHRulesService) {}

    public getRules(uuid: string): Observable<any> {
        return this.equatorProxyService
            .makeClareHomeRpcRequest$(uuid, ClareHomeRPCServiceNames.HAM2, HAM2_METHODS.GET_RULES, [{}, ""])
            .pipe(
                map((rules: []) =>
                    (rules || []).reduce((items, data: any) => {
                        if (!data.internal) {
                            items[data.rule_id] = CHRule.fromCHObject(data);
                        }
                        return items;
                    }, {})
                )
            );
    }

    public checkRuleTypes(rules: ClareHomeDbCollection<CHRule>): Observable<ClareHomeDbCollection<CHRule>> {
        return this.ruleService.getActivePanelCommands().pipe(
            map(commandState => {
                const ruleIds = Object.keys(rules);
                ruleIds.forEach(id => {
                    if (rules[id]["ruleType"] === "Automation") {
                        const command = this.getCommandById(rules[id]["commandId"], commandState);
                        const action = command?.result?.["steps"]?.[0]?.["action"];
                        if (action?.["commandType"] === "Apns") {
                            rules[id]["ruleType"] = "Notification";
                        }
                    }
                });
                return rules;
            })
        );
    }

    private getCommandById(commandId, commandState) {
        const commandForId = commandState?.result?.[commandId] ? Object.assign({}, commandState?.result?.[commandId]) : null;
        if (isSceneCommand(commandForId)) {
            const newSteps = {};
            let index = 0;
            Object.values(commandForId.steps).forEach(step => {
                newSteps[index] = {
                    action: commandState.result[step.action.command_id],
                    delay: step.delay,
                };
                index++;
            });
            commandForId.steps = newSteps;
        }
        return {
            uuid: commandState.uuid,
            callState: commandState.callState,
            result: commandForId,
        };
    }

    private prepareRuleData(rule: CHRuleInt) {
        const conditionHelper = conditionHelperMap[rule.conditionType];
        const conditionData = conditionHelper.exportObject(rule.condition, conditionHelper.importMap);
        if (conditionData.condition_type !== ConditionProviders.PROPERTY_CHANGE) {
            conditionData.name = rule.name;
        }
        const ruleData = ClassImporterExporter.exportObject(rule, CHRule.importMap, false);

        Object.values(rule.command.steps).forEach(stepValue => {
            let actionValue;
            delete stepValue.flyoutMenuActions;
            stepValue.enabled = true;
            if (stepValue.action.commandType === CommandProviders.SCENE && stepValue.action.commandId) {
                actionValue = {
                    commandId: stepValue.action.commandId,
                    commandType: stepValue.action.commandType,
                };
            } else {
                actionValue = stepValue.action;
            }
            stepValue.action = this.prepareCommandData(actionValue, rule);
        });

        ruleData.condition = conditionData;
        ruleData.command = this.prepareCommandData(rule.command, rule);
        return ruleData;
    }

    private prepareCommandData(command: any, rule) {
        const commandHelper = commandHelperMap[command.commandType];
        return commandHelper.exportObject(rule, command, commandHelper.importMap, false);
    }

    public createRule(uuid: string, rule: CHRuleInt): Observable<any> {
        const ruleData = this.prepareRuleData(rule);
        return this.equatorProxyService.makeClareHomeRpcRequest$(
            uuid,
            ClareHomeRPCServiceNames.CLARE_HOME_HAM,
            CLARE_HOME_HAM_METHODS.SAVE_RULE,
            [ruleData]
        );
    }

    public deleteRule(uuid: string, rule: CHRuleInt): Observable<any> {
        const src$ =
            rule.conditionType === ConditionProviders.UI_CONDITION
                ? this.equatorProxyService.makeClareHomeRpcRequest$(
                      uuid,
                      ClareHomeRPCServiceNames.CLARE_HOME_HAM,
                      CLARE_HOME_HAM_METHODS.FORCE_DELETE_SCENE,
                      [rule.commandId]
                  )
                : this.equatorProxyService.makeClareHomeRpcRequest$(
                      uuid,
                      ClareHomeRPCServiceNames.CLARE_HOME_HAM,
                      CLARE_HOME_HAM_METHODS.CASCADE_DELETE_RULE,
                      [rule.ruleId]
                  );

        return src$.pipe(map(() => ({ uuid, rule })));
    }
    public deleteAllRules(uuid: string, rules: CHRuleInt[]): Observable<any> {
        const requestDeleteRule = (_rule: CHRuleInt) =>
            _rule.conditionType === ConditionProviders.UI_CONDITION
                ? this.equatorProxyService.makeClareHomeRpcRequest$(
                      uuid,
                      ClareHomeRPCServiceNames.CLARE_HOME_HAM,
                      CLARE_HOME_HAM_METHODS.FORCE_DELETE_SCENE,
                      [_rule.commandId]
                  )
                : this.equatorProxyService.makeClareHomeRpcRequest$(
                      uuid,
                      ClareHomeRPCServiceNames.CLARE_HOME_HAM,
                      CLARE_HOME_HAM_METHODS.CASCADE_DELETE_RULE,
                      [_rule.ruleId]
                  );
        const deletedRules = rules.map(rule => requestDeleteRule(rule));

        return combineLatest(deletedRules);
    }
    public getCommands(uuid: string): Observable<ClareHomeDbCollection<CommandType>> {
        return this.equatorProxyService.makeClareHomeRpcRequest$(uuid, ClareHomeRPCServiceNames.HAM2, HAM2_METHODS.GET_ALL_COMMANDS).pipe(
            map((commands: CommandType[]) =>
                (commands || []).reduce((commandObject, command) => {
                    const steps = {};
                    let index = 0;
                    Object.values(command).forEach(value => {
                        // Builds up scene steps object
                        // Looks for values on the command that include a command_id
                        // Scene steps come in like 0: { command_id: ... }
                        if (value["command_id"]) {
                            steps[index] = { action: value, delay: value.delay };
                            index++;
                        }
                    });
                    const commandHelper = commandHelperMap[command["command_type"]] || CommandHelper;

                    const _command = commandHelper.fromCHObject(command, steps);
                    commandObject[_command.commandId] = _command;
                    return commandObject;
                }, {})
            ),
            catchError(error => of(error))
        );
    }

    public getConditions(uuid: string): Observable<ClareHomeDbCollection<ConditionType>> {
        return this.equatorProxyService.makeClareHomeRpcRequest$(uuid, ClareHomeRPCServiceNames.HAM2, HAM2_METHODS.GET_ALL_CONDITIONS).pipe(
            map((conditions: any[]) =>
                (conditions || []).reduce((conditionObject, condition) => {
                    const conditionHelper = conditionHelperMap[condition.condition_type] || ConditionHelper;
                    conditionObject[condition.condition_id] = conditionHelper.fromCHObject(condition);
                    return conditionObject;
                }, {})
            ),
            catchError(error => throwError(() => error))
        );
    }
}
